import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Play, AlertCircle, CheckCircle2, Activity, Database, Brain } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

type Config = {
  id: string;
  enabled: boolean;
  frequency_hours: number;
  firecrawl_monthly_budget: number;
  firecrawl_used_this_month: number;
  firecrawl_reset_date: string;
  last_run_at: string | null;
  next_run_at: string | null;
};

type Run = {
  id: string;
  started_at: string;
  completed_at: string | null;
  status: string;
  triggered_by: string;
  bots_checked: number;
  bots_updated: number;
  bots_disabled: number;
  bots_reenabled: number;
  firecrawl_credits_used: number;
  ai_calls_used: number;
  errors: Array<{ bot_name?: string; phase: string; message: string }>;
};

const FREQUENCY_OPTIONS = [
  { value: 12, label: "Every 12 hours" },
  { value: 24, label: "Daily" },
  { value: 48, label: "Every 2 days" },
  { value: 72, label: "Every 3 days" },
  { value: 168, label: "Weekly" },
];

export function AdminFreshnessControl() {
  const { toast } = useToast();
  const [config, setConfig] = useState<Config | null>(null);
  const [runs, setRuns] = useState<Run[]>([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    const [{ data: cfg }, { data: runRows }] = await Promise.all([
      supabase.from("bot_freshness_config").select("*").limit(1).maybeSingle(),
      supabase.from("bot_freshness_runs").select("*").order("started_at", { ascending: false }).limit(10),
    ]);
    setConfig(cfg as Config | null);
    setRuns(((runRows as unknown) as Run[]) || []);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const updateConfig = async (patch: Partial<Config>) => {
    if (!config) return;
    setSaving(true);
    const { error } = await supabase.from("bot_freshness_config").update(patch).eq("id", config.id);
    setSaving(false);
    if (error) {
      toast({ title: "Save failed", description: error.message, variant: "destructive" });
    } else {
      setConfig({ ...config, ...patch });
      toast({ title: "Settings saved" });
    }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const { data, error } = await supabase.functions.invoke("bot-freshness-runner", {
        body: { triggered_by: "manual", force: true },
      });
      if (error) throw error;
      toast({
        title: "Run complete",
        description: `Checked ${data.bots_checked} • Updated ${data.bots_updated} • Disabled ${data.bots_disabled}`,
      });
      await load();
    } catch (e) {
      toast({
        title: "Run failed",
        description: e instanceof Error ? e.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setRunning(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-12 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (!config) return null;

  const budgetUsedPct = Math.min(100, Math.round((config.firecrawl_used_this_month / config.firecrawl_monthly_budget) * 100));
  const budgetColor = budgetUsedPct >= 75 ? "bg-destructive" : budgetUsedPct >= 50 ? "bg-amber-500" : "bg-primary";
  const lastRun = runs[0];

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5" /> Live Chatbot Freshness
            </CardTitle>
            <CardDescription>
              Auto-checks every bot's website, refreshes their details, and hides ones that go offline.
            </CardDescription>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground">{config.enabled ? "On" : "Off"}</span>
            <Switch
              checked={config.enabled}
              disabled={saving}
              onCheckedChange={(v) => updateConfig({ enabled: v })}
            />
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Status row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="rounded-lg border p-3">
            <div className="text-xs text-muted-foreground">Last run</div>
            <div className="text-sm font-medium mt-1">
              {config.last_run_at ? formatDistanceToNow(new Date(config.last_run_at), { addSuffix: true }) : "Never"}
            </div>
          </div>
          <div className="rounded-lg border p-3">
            <div className="text-xs text-muted-foreground">Next run</div>
            <div className="text-sm font-medium mt-1">
              {config.enabled && config.next_run_at
                ? formatDistanceToNow(new Date(config.next_run_at), { addSuffix: true })
                : "Paused"}
            </div>
          </div>
          <div className="rounded-lg border p-3">
            <div className="text-xs text-muted-foreground">Last result</div>
            <div className="text-sm font-medium mt-1 flex items-center gap-1">
              {lastRun ? (
                lastRun.status === "completed" ? (
                  <><CheckCircle2 className="h-3 w-3 text-emerald-500" /> Completed</>
                ) : lastRun.status === "failed" ? (
                  <><AlertCircle className="h-3 w-3 text-destructive" /> Failed</>
                ) : (
                  <>Running…</>
                )
              ) : "—"}
            </div>
          </div>
          <div className="rounded-lg border p-3">
            <div className="text-xs text-muted-foreground">Bots updated last run</div>
            <div className="text-sm font-medium mt-1">{lastRun?.bots_updated ?? 0}</div>
          </div>
        </div>

        {/* Settings */}
        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Run frequency</label>
            <Select
              value={String(config.frequency_hours)}
              onValueChange={(v) => updateConfig({ frequency_hours: Number(v) })}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {FREQUENCY_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium flex items-center gap-1">
              <Database className="h-3 w-3" /> Firecrawl monthly budget
            </label>
            <Select
              value={String(config.firecrawl_monthly_budget)}
              onValueChange={(v) => updateConfig({ firecrawl_monthly_budget: Number(v) })}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="100">100 credits</SelectItem>
                <SelectItem value="200">200 credits</SelectItem>
                <SelectItem value="400">400 credits</SelectItem>
                <SelectItem value="800">800 credits</SelectItem>
                <SelectItem value="1500">1,500 credits</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Budget meter */}
        <div>
          <div className="flex justify-between text-xs mb-1">
            <span className="text-muted-foreground flex items-center gap-1">
              <Database className="h-3 w-3" /> Firecrawl this month
            </span>
            <span className="font-medium">
              {config.firecrawl_used_this_month} / {config.firecrawl_monthly_budget}
              {budgetUsedPct >= 50 && <span className="ml-2 text-amber-600">Falling back to Gemini only</span>}
            </span>
          </div>
          <div className="h-2 rounded-full bg-muted overflow-hidden">
            <div className={`h-full ${budgetColor} transition-all`} style={{ width: `${budgetUsedPct}%` }} />
          </div>
          <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
            <Brain className="h-3 w-3" /> When ≥50% of Firecrawl credits are used, enrichment uses Lovable AI (Gemini) only until next month.
          </p>
        </div>

        {/* Run now */}
        <div className="flex gap-2 pt-2 border-t">
          <Button onClick={runNow} disabled={running}>
            {running ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Running…</> : <><Play className="h-4 w-4 mr-2" />Run now</>}
          </Button>
          <Button variant="outline" onClick={load}>Refresh status</Button>
        </div>

        {/* Activity log */}
        <div className="pt-2 border-t">
          <h4 className="text-sm font-semibold mb-3">Recent activity</h4>
          {runs.length === 0 ? (
            <p className="text-sm text-muted-foreground">No runs yet.</p>
          ) : (
            <div className="space-y-2">
              {runs.map((r) => (
                <div key={r.id} className="rounded-lg border p-3 text-sm space-y-2">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2">
                      {r.status === "completed" && <Badge variant="secondary"><CheckCircle2 className="h-3 w-3 mr-1" />Completed</Badge>}
                      {r.status === "failed" && <Badge variant="destructive"><AlertCircle className="h-3 w-3 mr-1" />Failed</Badge>}
                      {r.status === "running" && <Badge>Running</Badge>}
                      <span className="text-xs text-muted-foreground">via {r.triggered_by}</span>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {formatDistanceToNow(new Date(r.started_at), { addSuffix: true })}
                    </span>
                  </div>
                  <div className="flex gap-3 text-xs text-muted-foreground flex-wrap">
                    <span>✓ {r.bots_checked} checked</span>
                    <span>↻ {r.bots_updated} updated</span>
                    {r.bots_disabled > 0 && <span className="text-amber-600">⊘ {r.bots_disabled} disabled</span>}
                    {r.bots_reenabled > 0 && <span className="text-emerald-600">↑ {r.bots_reenabled} re-enabled</span>}
                    <span>FC {r.firecrawl_credits_used}</span>
                    <span>AI {r.ai_calls_used}</span>
                  </div>
                  {r.errors && r.errors.length > 0 && (
                    <details className="text-xs">
                      <summary className="cursor-pointer text-destructive">
                        {r.errors.length} error{r.errors.length > 1 ? "s" : ""}
                      </summary>
                      <ul className="mt-1 space-y-1 pl-3">
                        {r.errors.slice(0, 10).map((e, i) => (
                          <li key={i} className="text-muted-foreground">
                            <span className="font-medium">{e.bot_name || "—"}</span> [{e.phase}]: {e.message}
                          </li>
                        ))}
                        {r.errors.length > 10 && <li className="italic">…and {r.errors.length - 10} more</li>}
                      </ul>
                    </details>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
